import { useNavigate } from 'react-router-dom';
import { Icon, Copy, Star } from '../ui/Icon';
import Spinner from '../ui/Spinner';
import { UserAvatar } from '../ui/UserAvatar';
import type { CommunityTripSummary } from '../../types';
import { getIataCode } from '../../utils/iata';
import { useTranslation } from 'react-i18next';
import { PhotoCredit } from '../ui/PhotoCredit';

const DEST_THEMES: [string, { from: string; to: string }][] = [
  ['paris',     { from: '#5c3a6e', to: '#2d1a3e' }],
  ['london',    { from: '#2a3547', to: '#141c28' }],
  ['new york',  { from: '#6b4228', to: '#3a2010' }],
  ['lisboa',    { from: '#1a5c6e', to: '#0b3240' }],
  ['lisbon',    { from: '#1a5c6e', to: '#0b3240' }],
  ['madrid',    { from: '#7a3320', to: '#3e1a0e' }],
  ['barcelona', { from: '#7a5c1a', to: '#3e2e0a' }],
  ['rome',      { from: '#7a4020', to: '#3e200e' }],
  ['tokyo',     { from: '#6e2845', to: '#3a1424' }],
  ['dubai',     { from: '#7a6218', to: '#3e320c' }],
  ['amsterdam', { from: '#1a547a', to: '#0b2c40' }],
  ['berlin',    { from: '#323845', to: '#181c24' }],
  ['prague',    { from: '#432878', to: '#22143e' }],
  ['vienna',    { from: '#1a5c48', to: '#0b3228' }],
  ['athens',    { from: '#1a4878', to: '#0b2440' }],
  ['bali',      { from: '#1a6038', to: '#0b321c' }],
];

function getTheme(destination: string) {
  const lower = destination.toLowerCase();
  const match = DEST_THEMES.find(([key]) => lower.includes(key));
  return match ? match[1] : { from: '#1e3048', to: '#0d1824' };
}

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
}

interface Props {
  trip: CommunityTripSummary;
  onClone: (id: string) => void;
  cloning?: boolean;
}

export function CommunityTripCard({ trip, onClone, cloning }: Props) {
  const { t } = useTranslation();
  const navigate   = useNavigate();
  const theme      = getTheme(trip.destination);
  const authorName = trip.owner.displayName || trip.owner.username;
  const initials   = authorName.slice(0, 2).toUpperCase();

  return (
    <article
      className="group relative rounded-2xl overflow-hidden cursor-pointer bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
      onClick={() => navigate(`/explore/${trip.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && navigate(`/explore/${trip.id}`)}
    >
      {/* Visual section */}
      <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>

        {trip.coverImageUrl ? (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${trip.coverImageUrl})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />
            {/* Bottom overlay: destination + expense */}
            <div className="absolute inset-x-0 bottom-0 p-3 flex items-end justify-between gap-2 pointer-events-none">
              <div className="min-w-0">
                <p className="font-mono text-[10px] font-semibold tracking-[0.16em] uppercase text-white/85 line-clamp-1">
                  {trip.destination}
                </p>
                <PhotoCredit
                  name={trip.coverImagePhotographer}
                  url={trip.coverImagePhotographerUrl}
                  className="block mt-0.5 pointer-events-auto"
                />
              </div>
              {trip.expenseTotal !== null && (
                <span className="font-mono text-[11px] font-semibold text-white/95 bg-black/35 backdrop-blur-sm rounded-md px-2 py-0.5 shrink-0">
                  {formatCurrency(trip.expenseTotal, trip.expenseCurrency)}
                </span>
              )}
            </div>
          </>
        ) : (
          <>
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(135deg, ${theme.from}, ${theme.to})` }}
            />
            <div
              className="absolute inset-0 opacity-[0.05] pointer-events-none"
              style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '16px 16px' }}
            />

            {/* Trip brief */}
            <div className="absolute inset-0 flex flex-col justify-between p-3.5">
              {/* Destination */}
              <p className="font-mono text-[10px] font-semibold tracking-[0.16em] uppercase text-white/55">
                {trip.destination}
              </p>

              <div className="flex flex-col gap-2.5">
                {/* Expense hero */}
                {trip.expenseTotal !== null ? (
                  <div>
                    <p
                      className="font-serif font-bold text-white leading-none"
                      style={{ fontSize: 30 }}
                    >
                      {formatCurrency(trip.expenseTotal, trip.expenseCurrency)}
                    </p>
                    <p className="font-mono text-[10px] text-white/50 mt-1 tracking-wide">total spent</p>
                  </div>
                ) : (
                  <p className="font-mono text-[10px] text-white/35 tracking-wide">no expenses logged</p>
                )}

                {/* Stats row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Stat value={trip.nights} label="nights" />
                    <Stat value={trip.participantCount} label="people" />
                    <Stat value={trip.activityCount} label="places" />
                  </div>
                  {trip.avgRating !== null && (
                    <div className="flex items-center gap-1">
                      <Icon as={Star} size={11} className="text-amber-400 fill-amber-400" />
                      <span className="font-mono text-[11px] font-semibold text-white/75">
                        {trip.avgRating.toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Clone button */}
        <button
          onClick={e => { e.stopPropagation(); onClone(trip.id); }}
          disabled={cloning}
          className="absolute top-2 right-2 w-7 h-7 rounded-lg bg-black/25 backdrop-blur-sm flex items-center justify-center text-white/80 hover:bg-white hover:text-slate-900 transition-all md:opacity-0 md:group-hover:opacity-100 cursor-pointer disabled:opacity-60"
          aria-label={t('explore.detail.useAsTemplate')}
        >
          {cloning ? <Spinner size="sm" /> : <Icon as={Copy} size={13} />}
        </button>
      </div>

      {/* Info strip */}
      <div className="px-3 pt-2.5 pb-3">
        <p
          className="font-serif font-bold text-base leading-snug text-slate-900 dark:text-slate-100 line-clamp-1 mb-2"
          style={{  }}
        >
          {trip.name}
        </p>

        <div className="flex items-center justify-between gap-2">
          {/* Author */}
          <div className="flex items-center gap-1.5 min-w-0">
            <UserAvatar name={authorName} avatarUrl={trip.owner.avatarUrl} size={16} />
            <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 truncate">@{authorName}</span>
          </div>

          {/* Right: stats + rating */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
              {trip.nights}n · {trip.participantCount}p · {trip.activityCount}✦
            </span>
            {trip.avgRating !== null && (
              <div className="flex items-center gap-0.5">
                <Icon as={Star} size={10} className="text-amber-400 fill-amber-400" />
                <span className="font-mono text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                  {trip.avgRating.toFixed(1)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span className="font-mono text-[13px] font-bold text-white/90 leading-none">{value}</span>
      <span className="font-mono text-[9px] uppercase tracking-widest text-white/45">{label}</span>
    </div>
  );
}
